import React, { useContext, useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { AuthContext } from "../context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaTrash, FaRegCopy, FaEdit } from "react-icons/fa";
import { Link } from "react-router-dom";
const UserProfile = () => {
  const { user, login } = useContext(AuthContext);
  const [userData, setUserData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [appsError, setAppsError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const { password, ...rest } = user;
      setUserData(rest);
      fetchUserApplicationsByEmail(user.email);
    }
  }, [user]);

  async function fetchUserApplicationsByEmail(email) {
    try {
      setLoadingApps(true);
      setAppsError(null);
      const res = await fetch(
        `http://localhost:5000/api/applications/email/${email}`
      );
      if (!res.ok) throw new Error("Failed to fetch applications");
      const data = await res.json();
      setApplications(Array.isArray(data) ? data : data.applications || []);
    } catch (err) {
      setAppsError(err.message);
    } finally {
      setLoadingApps(false);
    }
  }

  const handleCopyAppId = (appId) => {
    navigator.clipboard.writeText(appId).then(() => {
      toast.success("Application ID copied to clipboard!");
    });
  };
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!userData.name || !userData.email) {
      toast.warn("Name and Email are required.");
      return;
    }

    // Only send password if it's filled
    const { name, email, password } = userData;
    const updatePayload = { name, email };
    if (password && password.trim() !== "") {
      updatePayload.password = password;
    }

    try {
      const response = await fetch(`http://localhost:5000/user/${user._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatePayload),
      });

      let data;

      try {
        data = await response.json();
      } catch {
        const text = await response.text();
        throw new Error(text || "Server returned non-JSON response.");
      }

      if (!response.ok) {
        toast.error(`Update failed: ${data.error || response.statusText}`);
        return;
      }

      toast.success("Profile updated successfully!");
      login(data.user);
      setUserData((prev) => ({ ...prev, password: "" }));
    } catch (error) {
      console.error("Update error:", error);
      toast.error("An error occurred while updating profile.");
    }
  };

  const handleDeleteApp = async (appId) => {
    if (!window.confirm("Are you sure you want to delete this application?"))
      return;

    try {
      const res = await fetch(
        `http://localhost:5000/api/application/${appId}`,
        {
          method: "DELETE",
        }
      );

      if (!res.ok) throw new Error("Delete failed");

      setApplications((prev) => prev.filter((app) => app._id !== appId));
      toast.success("Application deleted");
    } catch {
      toast.error("Failed to delete application");
    }
  };

  if (!user) return <Navigate to="/Login" replace />;
  if (!userData) return <ProfileWrapper>Loading profile...</ProfileWrapper>;

  return (
    <PageWrapper>
      <ProfileWrapper>
        <h2>User Profile</h2>

        <form onSubmit={handleUpdate}>
          <Section>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              value={userData.name || ""}
              onChange={(e) =>
                setUserData({ ...userData, name: e.target.value })
              }
              required
            />
          </Section>

          <Section>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={userData.email || ""}
              onChange={(e) =>
                setUserData({ ...userData, email: e.target.value })
              }
              required
            />
          </Section>

          <Section>
            <Label htmlFor="password">Change Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter new password"
              value={userData.password || ""}
              onChange={(e) =>
                setUserData({ ...userData, password: e.target.value })
              }
              autoComplete="new-password"
            />
          </Section>

          <Button type="submit">Update Profile</Button>
        </form>
      </ProfileWrapper>

      <ApplicationsWrapper>
        <h2>Your Submitted Applications</h2>

        {loadingApps && <p>Loading applications...</p>}
        {appsError && <ErrorMsg>{appsError}</ErrorMsg>}
        {!loadingApps && applications.length === 0 && (
          <p>
            No applications found. <Link to="/">Click here to apply now.</Link>
          </p>
        )}

        {!loadingApps && applications.length > 0 && (
          <TableWrapper>
            <table>
              <thead>
                <tr>
                  <th>Application ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app._id}>
                    <td title={app.applicationId}>
                      {app.applicationId}{" "}
                      <CopyIcon
                        onClick={() => handleCopyAppId(app.applicationId)}
                        title="Copy Application ID"
                      />
                    </td>
                    <td>{app.name || "-"}</td>
                    <td>{app.email || "-"}</td>
                    <td>
                      <ActionButton
                        onClick={() => handleDeleteApp(app._id)}
                        $delete
                        title="Delete"
                      >
                        <FaTrash />
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrapper>
        )}
      </ApplicationsWrapper>
    </PageWrapper>
  );
};

export default UserProfile;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  max-width: 900px;
  margin: 3rem auto;
  padding: 1rem;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  color: #e6e6e6;
`;

const ProfileWrapper = styled.div`
  background: linear-gradient(145deg, #0d0d0d, #1a1a1a);
  border-radius: 8px;
  border: 1px solid #1f1f21;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  animation: ${fadeIn} 0.5s ease;
  margin-bottom: 3rem;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #1f1f21;
  background-color: transparent;
  color: #e6e6e6;
  border-radius: 4px;

  &:focus {
    border-color: #3f51ff;
    outline: none;
  }
`;

const Button = styled.button`
  background: linear-gradient(90deg, #194bfd 0%, #ad13fb 100%);
  color: #ffffff;
  transition: all 0.3s ease;
  padding: 0.75rem 1.5rem;
  border: none;
  margin-top: 1rem;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
  font-weight: bold;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(90, 24, 154, 0.5);
  }
`;

const ApplicationsWrapper = styled.div`
  background: linear-gradient(145deg, #0d0d0d, #1a1a1a);
  border-radius: 8px;
  border: 1px solid #1f1f21;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  animation: ${fadeIn} 0.5s ease;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  table {
    width: 100%;
    border-collapse: collapse;
    color: #e6e6e6;

    th,
    td {
      padding: 12px 15px;
      text-align: left;
      border-bottom: 1px solid #333;
    }

    th {
      background-color: #007bff;
      color: white;
    }

    tr:hover {
      background-color: #222;
    }
  }
`;

const ActionButton = styled.button`
  background-color: ${(props) => (props.$delete ? "#dc3545" : "#007bff")};
  border: none;
  color: white;
  padding: 6px 12px;
  margin-right: 8px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  font-size: 1rem;

  &:hover {
    opacity: 0.85;
  }
`;

const CopyIcon = styled(FaRegCopy)`
  cursor: pointer;
  margin-left: 8px;
  color: #4caf50;
  vertical-align: middle;

  &:hover {
    color: #81c784;
  }
`;

const ErrorMsg = styled.p`
  color: #dc3545;
  font-weight: bold;
`;
